import {computed, Injectable, signal} from '@angular/core';
import {
  ChartPoint,
  EventBank,
  ChartSegment,
  MarketSeries,
  MarketSeriesDefinition,
  MarketSeriesKey,
  MarketWeek,
  Meeting,
  MonthCell,
  MonthDot,
  PolicyDataset,
  YearRow,
} from './policy-chart.models';

// Plot geometry, in the chart's 1200-unit viewBox. The month grid lines its
// columns up with these, so they live in one place.
export const PLOT_LEFT = 44;
export const PLOT_WIDTH = 1112;

/** A 1–10 score to a y coordinate: 1 sits at the baseline, 10 near the top. */
export const scoreY = (score: number): number => 188 - ((score - 1) / 9) * 156;

/** Where a date falls horizontally within a fiscal-year row. */
export const xForDate = (isoDate: string, start: number, end: number): number =>
  PLOT_LEFT + ((Date.parse(`${isoDate}T00:00:00Z`) - start) / (end - start)) * PLOT_WIDTH;

/** Federal fiscal year: October through September. */
const FISCAL_MONTHS = [10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const fiscalYearOf = (isoDate: string): number => {
  const year = Number(isoDate.slice(0, 4));
  return Number(isoDate.slice(5, 7)) >= 10 ? year + 1 : year;
};

/** A central bank whose meetings or rate changes can be overlaid. */
export interface PolicyBankDefinition {
  key: EventBank;
  /** What the rate table calls it, spelled out. */
  name: string;
  label: string;
  /** Full name of the source, shown as the toggle's tooltip. */
  description: string;
  color: string;
  /** The Fed is the subject of the chart, so it has no toggle. */
  alwaysOn: boolean;
  /**
   * BIS reference area, which is how this bank's changes are found in the
   * dataset. Absent for the two banks that carry their own calendar instead: the
   * Fed, read from the Federal Reserve, and the BOJ, whose changes are attributed
   * to its statements.
   */
  area?: string;
}

/**
 * Owns the market feeds. Every feed is declared once here, loaded through the
 * same path, and shown or hidden by this service — so adding a feed is one entry
 * in the registry rather than a new branch in each component.
 */
@Injectable({providedIn: 'root'})
export class MarketSeriesManager {
  readonly definitions: readonly MarketSeriesDefinition[] = [
    {
      key: 'spy',
      label: 'SPY',
      description: 'SPDR S&P 500 ETF Trust via Nasdaq',
      statLabel: 'S&P',
      icon: '📈',
      color: '#111',
      dash: null,
      primary: true,
      monthDots: true,
      dotRadius: 2.7,
      decimals: 0,
      weeksOf: (dataset) => dataset.weeks,
    },
    {
      key: 'gold',
      label: 'GLD gold',
      description: 'SPDR Gold Shares via Nasdaq',
      statLabel: 'Gold',
      icon: '🪙',
      color: '#b8860b',
      dash: '5 3',
      primary: false,
      monthDots: true,
      dotRadius: 2.7,
      decimals: 2,
      weeksOf: (dataset) => dataset.goldWeeks,
    },
    {
      key: 'oil',
      label: 'USO oil',
      description: 'United States Oil Fund via Nasdaq',
      statLabel: 'Oil',
      icon: '🛢',
      color: '#6b4f2a',
      dash: '2 2',
      primary: false,
      monthDots: false,
      dotRadius: 1.9,
      decimals: 2,
      weeksOf: (dataset) => dataset.oilWeeks,
    },
    {
      key: 'unemployment',
      label: 'US unemployment',
      description: 'Civilian unemployment rate via FRED (UNRATE). Published monthly, so this line steps once a month rather than weekly.',
      statLabel: 'Jobless',
      icon: '👷',
      color: '#0e7490',
      dash: '6 2',
      primary: false,
      monthDots: false,
      dotRadius: 1.9,
      decimals: 1,
      weeksOf: (dataset) => dataset.unemploymentWeeks,
    },
    {
      key: 'dollar',
      label: 'Dollar index',
      description: 'Nominal Broad U.S. Dollar Index via FRED',
      statLabel: 'Dollar',
      icon: '💵',
      color: '#b5179e',
      dash: '4 3',
      primary: false,
      monthDots: false,
      dotRadius: 1.9,
      decimals: 2,
      weeksOf: (dataset) => dataset.dollarWeeks,
    },
  ];

  /**
   * One categorical palette for every bank, not a colour picked per bank as each
   * was added. National colours are left to the flag in the label: most of these
   * flags are red or blue, and reusing them put near-identical reds and blues on
   * the same row.
   *
   * The first fifteen are a validated categorical set, ordered so that neighbours
   * stay apart in *both* orders a reader meets them in — this registry, which is
   * the legend, and the alphabetical order of the rate table. The rest are stepped
   * around the hue wheel by farthest-point search against the ones already taken.
   *
   * At this count colour can no longer carry identity on its own, and it is not
   * asked to: the closest pair in the set is ΔE 6.6 (OKLab ×100), well under the
   * floor of 15, and no set of forty can do better — the wheel holds perhaps a
   * dozen distinguishable hues. Every mark is therefore labelled — the legend, the
   * rate row, the tooltip on each flag — and colour only groups a bank's marks
   * with each other. Several of these steps also fall below 3:1 against white,
   * which those same labels are what makes readable.
   */
  readonly bankDefinitions: readonly PolicyBankDefinition[] = [
    {key: 'FED', name: 'Federal Reserve', label: 'FED', description: '🇺🇸 Federal Reserve (FOMC meetings) — arguably the most important globally.', color: '#b3251f', alwaysOn: true},
    {key: 'BOJ', name: 'Bank of Japan', label: '🇯🇵 BOJ meetings', description: '🇯🇵 Bank of Japan (BOJ)', color: '#00a2c7', alwaysOn: false},
    {key: 'PBOC', name: 'People\'s Bank of China', label: '🇨🇳 China', description: '🇨🇳 People\'s Bank of China (PBOC) — MLF, LPR, RRR, liquidity operations.', color: '#008300', alwaysOn: false, area: 'CN'},
    {key: 'RBI', name: 'Reserve Bank of India', label: '🇮🇳 RBI', description: '🇮🇳 Reserve Bank of India (RBI)', color: '#eda100', alwaysOn: false, area: 'IN'},
    {key: 'BOC', name: 'Bank of Canada', label: '🇨🇦 BOC', description: '🇨🇦 Bank of Canada (BOC)', color: '#2a78d6', alwaysOn: false, area: 'CA'},
    {key: 'SNB', name: 'Swiss National Bank', label: '🇨🇭 SNB', description: '🇨🇭 Swiss National Bank (SNB)', color: '#1baf7a', alwaysOn: false, area: 'CH'},
    {key: 'BOE', name: 'Bank of England', label: '🇬🇧 BOE', description: '🇬🇧 Bank of England (BOE)', color: '#eb6834', alwaysOn: false, area: 'GB'},
    {key: 'ECB', name: 'European Central Bank', label: '🇪🇺 ECB', description: '🇪🇺 European Central Bank (ECB)', color: '#4a3aa7', alwaysOn: false, area: 'XM'},
    {key: 'RBA', name: 'Reserve Bank of Australia', label: '🇦🇺 RBA', description: '🇦🇺 Reserve Bank of Australia (RBA)', color: '#e87ba4', alwaysOn: false, area: 'AU'},
    {key: 'RIKSBANK', name: 'Sveriges Riksbank', label: '🇸🇪 Riksbank', description: '🇸🇪 Sveriges Riksbank (Riksbank) — SEK policy rate.', color: '#9c6114', alwaysOn: false, area: 'SE'},
    {key: 'NORGES', name: 'Norges Bank', label: '🇳🇴 Norges Bank', description: '🇳🇴 Norges Bank — NOK policy rate.', color: '#d6337d', alwaysOn: false, area: 'NO'},
    {key: 'BOK', name: 'Bank of Korea', label: '🇰🇷 BOK', description: '🇰🇷 Bank of Korea (BOK) — KRW base rate.', color: '#7f8c00', alwaysOn: false, area: 'KR'},
    {key: 'BCB', name: 'Central Bank of Brazil', label: '🇧🇷 BCB', description: '🇧🇷 Central Bank of Brazil (BCB) — Selic target rate.', color: '#a6a1fd', alwaysOn: false, area: 'BR'},
    {key: 'BANXICO', name: 'Bank of Mexico', label: '🇲🇽 Banxico', description: '🇲🇽 Bank of Mexico (Banxico) — overnight target rate.', color: '#c059e9', alwaysOn: false, area: 'MX'},
    {key: 'BI', name: 'Bank of Indonesia', label: '🇮🇩 BI', description: '🇮🇩 Bank of Indonesia (BI) — BI rate.', color: '#90c205', alwaysOn: false, area: 'ID'},
    {key: 'BCRA', name: 'Central Bank of Argentina', label: '🇦🇷 BCRA', description: '🇦🇷 Central Bank of Argentina (BCRA)', color: '#852e6d', alwaysOn: false, area: 'AR'},
    {key: 'BCCH', name: 'Central Bank of Chile', label: '🇨🇱 BCCh', description: '🇨🇱 Central Bank of Chile (BCCh)', color: '#9d65a2', alwaysOn: false, area: 'CL'},
    {key: 'BANREP', name: 'Bank of the Republic, Colombia', label: '🇨🇴 BanRep', description: '🇨🇴 Bank of the Republic, Colombia (BanRep)', color: '#7885f8', alwaysOn: false, area: 'CO'},
    {key: 'CNB', name: 'Czech National Bank', label: '🇨🇿 CNB', description: '🇨🇿 Czech National Bank (CNB)', color: '#ed70de', alwaysOn: false, area: 'CZ'},
    {key: 'DN', name: 'Danmarks Nationalbank', label: '🇩🇰 Nationalbanken', description: '🇩🇰 Danmarks Nationalbank', color: '#db3931', alwaysOn: false, area: 'DK'},
    {key: 'HKMA', name: 'Hong Kong Monetary Authority', label: '🇭🇰 HKMA', description: '🇭🇰 Hong Kong Monetary Authority (HKMA)', color: '#3cbdb9', alwaysOn: false, area: 'HK'},
    {key: 'HNB', name: 'Croatian National Bank', label: '🇭🇷 HNB', description: '🇭🇷 Croatian National Bank (HNB)', color: '#4eb0f9', alwaysOn: false, area: 'HR'},
    {key: 'MNB', name: 'Magyar Nemzeti Bank', label: '🇭🇺 MNB', description: '🇭🇺 Magyar Nemzeti Bank (MNB)', color: '#9b4b66', alwaysOn: false, area: 'HU'},
    {key: 'BOI', name: 'Bank of Israel', label: '🇮🇱 BOI', description: '🇮🇱 Bank of Israel (BOI)', color: '#3fad1e', alwaysOn: false, area: 'IL'},
    {key: 'CBI', name: 'Central Bank of Iceland', label: '🇮🇸 CBI', description: '🇮🇸 Central Bank of Iceland (CBI)', color: '#f24e71', alwaysOn: false, area: 'IS'},
    {key: 'CBK', name: 'Central Bank of Kuwait', label: '🇰🇼 CBK', description: '🇰🇼 Central Bank of Kuwait (CBK)', color: '#7463ed', alwaysOn: false, area: 'KW'},
    {key: 'BAM', name: 'Bank Al-Maghrib', label: '🇲🇦 BAM', description: '🇲🇦 Bank Al-Maghrib (BAM), Morocco', color: '#e154b0', alwaysOn: false, area: 'MA'},
    {key: 'NBRNM', name: 'National Bank of North Macedonia', label: '🇲🇰 NBRNM', description: '🇲🇰 National Bank of North Macedonia (NBRNM)', color: '#9c1143', alwaysOn: false, area: 'MK'},
    {key: 'BNM', name: 'Bank Negara Malaysia', label: '🇲🇾 BNM', description: '🇲🇾 Bank Negara Malaysia (BNM)', color: '#26c858', alwaysOn: false, area: 'MY'},
    {key: 'RBNZ', name: 'Reserve Bank of New Zealand', label: '🇳🇿 RBNZ', description: '🇳🇿 Reserve Bank of New Zealand (RBNZ)', color: '#1a97f4', alwaysOn: false, area: 'NZ'},
    {key: 'BCRP', name: 'Central Reserve Bank of Peru', label: '🇵🇪 BCRP', description: '🇵🇪 Central Reserve Bank of Peru (BCRP)', color: '#3959da', alwaysOn: false, area: 'PE'},
    {key: 'BSP', name: 'Bangko Sentral ng Pilipinas', label: '🇵🇭 BSP', description: '🇵🇭 Bangko Sentral ng Pilipinas (BSP)', color: '#f38275', alwaysOn: false, area: 'PH'},
    {key: 'NBP', name: 'National Bank of Poland', label: '🇵🇱 NBP', description: '🇵🇱 National Bank of Poland (NBP)', color: '#be1253', alwaysOn: false, area: 'PL'},
    {key: 'BNR', name: 'National Bank of Romania', label: '🇷🇴 BNR', description: '🇷🇴 National Bank of Romania (BNR)', color: '#c587f9', alwaysOn: false, area: 'RO'},
    {key: 'NBS', name: 'National Bank of Serbia', label: '🇷🇸 NBS', description: '🇷🇸 National Bank of Serbia (NBS)', color: '#1745c2', alwaysOn: false, area: 'RS'},
    {key: 'CBR', name: 'Central Bank of Russia', label: '🇷🇺 CBR', description: '🇷🇺 Central Bank of Russia (CBR)', color: '#a071fc', alwaysOn: false, area: 'RU'},
    {key: 'SAMA', name: 'Saudi Central Bank', label: '🇸🇦 SAMA', description: '🇸🇦 Saudi Central Bank (SAMA)', color: '#88518c', alwaysOn: false, area: 'SA'},
    {key: 'BOT', name: 'Bank of Thailand', label: '🇹🇭 BOT', description: '🇹🇭 Bank of Thailand (BOT)', color: '#bd557a', alwaysOn: false, area: 'TH'},
    {key: 'CBRT', name: 'Central Bank of Türkiye', label: '🇹🇷 CBRT', description: '🇹🇷 Central Bank of Türkiye (CBRT)', color: '#1ba79f', alwaysOn: false, area: 'TR'},
    {key: 'SARB', name: 'South African Reserve Bank', label: '🇿🇦 SARB', description: '🇿🇦 South African Reserve Bank (SARB)', color: '#8669c6', alwaysOn: false, area: 'ZA'},
  ];

  /** The banks read from BIS, i.e. everything but the two with their own calendar. */
  readonly bisBanks = computed(() =>
    this.bankDefinitions.filter((bank): bank is PolicyBankDefinition & {area: string} =>
      bank.area !== undefined));

  private readonly hiddenKeys = signal<ReadonlySet<MarketSeriesKey>>(new Set());
  private readonly hiddenBanks = signal<ReadonlySet<EventBank>>(new Set());

  /** The definitions currently switched on, in registry order. */
  readonly visibleDefinitions = computed(() =>
    this.definitions.filter((definition) => !this.hiddenKeys().has(definition.key)));

  /** The banks currently switched on. */
  readonly visibleBanks = computed<EventBank[]>(() =>
    this.bankDefinitions
      .filter((bank) => !this.hiddenBanks().has(bank.key))
      .map((bank) => bank.key));

  /** Whether every bank overlay, including the Fed, is switched on. */
  readonly allBanksVisible = computed(() =>
    this.bankDefinitions.every((bank) => !this.hiddenBanks().has(bank.key)));

  /** Toggleable banks, i.e. everything the legend offers a button for. */
  readonly toggleableBanks = computed(() =>
    this.bankDefinitions.filter((bank) => !bank.alwaysOn));


  isVisible(key: MarketSeriesKey): boolean {
    return !this.hiddenKeys().has(key);
  }

  isBankVisible(key: EventBank): boolean {
    return this.visibleBanks().includes(key);
  }

  toggleBank(key: EventBank): void {
    const next = new Set(this.hiddenBanks());
    if (!next.delete(key)) next.add(key);
    this.hiddenBanks.set(next);
  }

  /** Switch every central-bank overlay on or off in one action. */
  toggleAllBanks(): void {
    this.hiddenBanks.set(
      this.allBanksVisible()
        ? new Set(this.bankDefinitions.map((bank) => bank.key))
        : new Set(),
    );
  }

  bankColor(key: EventBank): string {
    return this.bankDefinitions.find((bank) => bank.key === key)?.color ?? '#111';
  }

  /** The bank spelled out, for the rate table's row headers. */
  bankName(key: EventBank): string {
    return this.bankDefinitions.find((bank) => bank.key === key)?.name ?? key;
  }

  /** Whether an event or marker should be drawn at all. */
  showsEvent(bank: EventBank): boolean {
    return this.isBankVisible(bank);
  }

  toggle(key: MarketSeriesKey): void {
    const next = new Set(this.hiddenKeys());
    if (!next.delete(key)) next.add(key);
    this.hiddenKeys.set(next);
  }

  /** The feeds of a row that are currently switched on. */
  visibleSeries(row: YearRow): MarketSeries[] {
    return row.series.filter((series) => this.isVisible(series.definition.key));
  }

  /** The markers of a month cell whose feed is currently switched on. */
  visibleDots(month: MonthCell): MonthDot[] {
    return month.dots.filter((dot) => this.isVisible(dot.key));
  }

  /** Every fiscal year any feed has data for, newest first, capped at ten. */
  fiscalYears(dataset: PolicyDataset, currentFiscalYear: number): number[] {
    const fromFeeds = this.definitions.flatMap((definition) =>
      definition.weeksOf(dataset).map((week) => fiscalYearOf(week.lastObservation)));
    return [...new Set([
      ...fromFeeds,
      ...dataset.meetings.map((meeting) => fiscalYearOf(meeting.startDate)),
      ...dataset.rateActions.map((action) => fiscalYearOf(action.date)),
    ])]
      .filter((year) => year <= currentFiscalYear && year >= currentFiscalYear - 9)
      .sort((first, second) => second - first);
  }

  /**
   * Load every feed for one fiscal year. The 1–10 position of each week is scored
   * server-side, against the previous and current years' extremes together, so
   * every feed is on one rule; the low and high kept here are the year's own, for
   * the header stat.
   * Only the primary feed's segments carry FOMC context.
   */
  load(dataset: PolicyDataset, year: number, meetings: Meeting[]): MarketSeries[] {
    const start = Date.UTC(year - 1, 9, 1);
    const end = Date.UTC(year, 9, 1);
    return this.definitions.map((definition) => {
      const weeks = definition.weeksOf(dataset)
        .filter((week) => fiscalYearOf(week.lastObservation) === year);
      const low = weeks.length ? Math.min(...weeks.map((week) => week.close)) : undefined;
      const high = weeks.length ? Math.max(...weeks.map((week) => week.close)) : undefined;
      const points = this.chartPoints(weeks, start, end);
      return {
        definition,
        low,
        high,
        points,
        segments: this.chartSegments(points, definition.primary ? meetings : []),
      };
    });
  }

  /** The twelve month cells of a row, with a marker per feed that draws them. */
  loadMonths(dataset: PolicyDataset, series: MarketSeries[], year: number): MonthCell[] {
    const start = Date.UTC(year - 1, 9, 1);
    const end = Date.UTC(year, 9, 1);
    return FISCAL_MONTHS.map((month, index) => {
      const calendarYear = month >= 10 ? year - 1 : year;
      const prefix = `${calendarYear}-${String(month).padStart(2, '0')}`;
      const monthCenter = start + ((index + 0.5) / 12) * (end - start);
      const meetings = dataset.meetings.filter((meeting) => meeting.startDate.startsWith(prefix));
      const dots = series.flatMap((feed): MonthDot[] => {
        if (!feed.definition.monthDots) return [];
        const nearest = feed.definition.weeksOf(dataset)
          .filter((week) => week.lastObservation.startsWith(prefix))
          .sort((first, second) =>
            Math.abs(Date.parse(`${first.lastObservation}T00:00:00Z`) - monthCenter) -
            Math.abs(Date.parse(`${second.lastObservation}T00:00:00Z`) - monthCenter))[0];
        const score = nearest?.score;
        if (score === undefined || score === null) return [];
        return [{
          key: feed.definition.key,
          color: feed.definition.color,
          primary: feed.definition.primary,
          y: scoreY(score),
          score,
        }];
      });
      return {
        month,
        x: PLOT_LEFT + ((index + 0.5) / 12) * PLOT_WIDTH,
        meetingCount: meetings.length,
        hasProjections: meetings.some((meeting) => meeting.hasEconomicProjections),
        dots,
      };
    });
  }

  private chartPoints(
    weeks: MarketWeek[],
    start: number,
    end: number,
  ): ChartPoint[] {
    return weeks.flatMap((week) => {
      const score = week.score;
      if (score === null || !Number.isFinite(score)) return [];
      return [{
        x: xForDate(week.lastObservation, start, end),
        y: scoreY(score),
        score,
        value: week.close,
        weekStart: week.weekStart,
      }];
    });
  }

  private chartSegments(points: ChartPoint[], meetings: Meeting[]): ChartSegment[] {
    return points.slice(1).map((point, index) => {
      const previous = points[index];
      const beforePrevious = points[index - 1] ?? previous;
      const afterPoint = points[index + 2] ?? point;
      const pointTime = Date.parse(`${point.weekStart}T00:00:00Z`);
      const preFed = meetings.some((meeting) => {
        const days = (Date.parse(`${meeting.startDate}T00:00:00Z`) - pointTime) / 86_400_000;
        return days >= 0 && days <= 28;
      });
      return {
        x1: previous.x,
        y1: previous.y,
        x2: point.x,
        y2: point.y,
        preFed,
        momentum: point.score - previous.score,
        path: [
          `M ${previous.x} ${previous.y}`,
          `C ${previous.x + (point.x - beforePrevious.x) / 6} ${previous.y + (point.y - beforePrevious.y) / 6}`,
          `${point.x - (afterPoint.x - previous.x) / 6} ${point.y - (afterPoint.y - previous.y) / 6}`,
          `${point.x} ${point.y}`,
        ].join(' '),
      };
    });
  }
}
